import { FC, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { GlobalPresenceBand } from "@/lib/scoring/types";
import { CountryInput } from "@/components/filters/Locations/CountryInput";
import { RegionInput } from "@/components/filters/Locations/RegionInput";

type SubLocationType = "country" | "region";

interface SubInputProps {
  band: GlobalPresenceBand;
  onUpdate: (patch: Partial<GlobalPresenceBand>) => void;
  onClear: () => void;
}

export const SubsidiaryInput: FC<SubInputProps> = ({
  band,
  onUpdate,
  onClear,
}) => {
  const sub = band.subsidiaryLocations ?? {};
  const [locationType, setLocationType] = useState<SubLocationType>(() =>
    (sub.regions?.length ?? 0) > 0 ? "region" : "country"
  );

  const handleLocationTypeChange = (newType: SubLocationType) => {
    setLocationType(newType);
    onUpdate({
      subsidiaryLocations: { ...sub, countryCodes: [], regions: [] },
    });
  };

  const handleInclusionMethodChange = (value: string) => {
    const isAtLeast = value.startsWith("AT_LEAST_");
    if (isAtLeast) {
      const n = parseInt(value.split("_")[2], 10);
      onUpdate({
        subsidiaryLocations: {
          ...sub,
          inclusionMethod: "ANY_PRESENT",
          countMatching: n,
        },
      });
    } else {
      onUpdate({
        subsidiaryLocations: {
          ...sub,
          inclusionMethod: value as "ANY_PRESENT" | "ALL_PRESENT",
          countMatching: undefined,
        },
      });
    }
  };

  const inclusionSelectValue = sub.countMatching
    ? `AT_LEAST_${sub.countMatching}`
    : (sub.inclusionMethod ?? "ANY_PRESENT");

  return (
    <Box
      sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: 0.5 }}
        >
          SUBSIDIARY LOCATIONS
        </Typography>
        <IconButton
          size="small"
          onClick={onClear}
          aria-label="Remove subsidiary locations"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Subsidiaries by
        </Typography>
        <Button
          size="small"
          onClick={() =>
            handleLocationTypeChange(
              locationType === "country" ? "region" : "country"
            )
          }
          sx={{
            color: "primary.main",
            fontWeight: 700,
            fontSize: 13,
            p: 0,
            minWidth: 0,
            textTransform: "none",
          }}
        >
          {locationType === "country" ? "Country ▾" : "Region ▾"}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Match companies that operate in
        </Typography>
        <Select
          size="small"
          value={inclusionSelectValue}
          onChange={(e) => handleInclusionMethodChange(e.target.value)}
          sx={{ minWidth: 90, fontSize: 13 }}
        >
          <MenuItem value="ANY_PRESENT">Any</MenuItem>
          <MenuItem value="ALL_PRESENT">All</MenuItem>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <MenuItem key={n} value={`AT_LEAST_${n}`}>
              At Least {n}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2" color="text.secondary">
          {locationType === "country"
            ? "of the specified countries within their hierarchy"
            : "of the selected regions"}
        </Typography>
      </Box>

      {locationType === "country" ? (
        <CountryInput
          selectedItems={sub.countryCodes ?? []}
          onCountrySelect={(countryCodes) =>
            onUpdate({ subsidiaryLocations: { ...sub, countryCodes } })
          }
          isMultiple
          placeholderText="Subsidiary countries…"
        />
      ) : (
        <RegionInput
          selectedItems={sub.regions ?? []}
          onRegionSelect={(regions) =>
            onUpdate({ subsidiaryLocations: { ...sub, regions } })
          }
        />
      )}
    </Box>
  );
};
